<template>
    <div class="editor">
        <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }">
            <div class="menubar">
                <button
                    class="menubar__button"
                    :class="{ 'is-active': isActive.bold() }"
                    @click="commands.bold"
                >
                    <i class="material-icons">format_bold</i>
                </button>

                <button
                    class="menubar__button"
                    :class="{ 'is-active': isActive.italic() }"
                    @click="commands.italic"
                >
                    <i class="material-icons">format_italic</i>
                </button>

                <button
                    class="menubar__button"
                    :class="{ 'is-active': isActive.strike() }"
                    @click="commands.strike"
                >
                    <i class="material-icons">format_strikethrough</i>
                </button>

                <button
                    class="menubar__button"
                    :class="{ 'is-active': isActive.underline() }"
                    @click="commands.underline"
                >
                    <i class="material-icons">format_underlined</i>
                </button>
                <button
                    class="menubar__button"
                    :class="{ 'is-active': isActive.heading({ level: 1 }) }"
                    @click="commands.heading({ level: 1 })"
                >
                    H1
                </button>

                <button
                    class="menubar__button"
                    :class="{ 'is-active': isActive.heading({ level: 2 }) }"
                    @click="commands.heading({ level: 2 })"
                >
                    H2
                </button>

                <button
                    class="menubar__button"
                    :class="{ 'is-active': isActive.heading({ level: 3 }) }"
                    @click="commands.heading({ level: 3 })"
                >
                    H3
                </button>

                <button
                    class="menubar__button"
                    :class="{ 'is-active': isActive.bullet_list() }"
                    @click="commands.bullet_list"
                >
                    <i class="material-icons">format_list_bulleted</i>
                </button>

                <button class="menubar__button" @click="commands.undo">
                    <i class="material-icons">undo</i>
                </button>

                <button class="menubar__button" @click="commands.redo">
                    <i class="material-icons">redo</i>
                </button>
            </div>
        </editor-menu-bar>
        <editor-content
            @onUpdate="onInput"
            @input="onInput"
            class="editor__content"
            :class="{ '-placeholder': value == '' }"
            ref="editor-content"
            :data-content="$t('PLACEHOLDER_TYPE_HERE')"
            :editor="editor"
        />
    </div>
</template>
<script>
import { Editor, EditorContent, EditorMenuBar } from 'tiptap'
import {
    Blockquote,
    CodeBlock,
    HardBreak,
    Heading,
    HorizontalRule,
    OrderedList,
    BulletList,
    ListItem,
    TodoItem,
    TodoList,
    Bold,
    Code,
    Italic,
    Link,
    Strike,
    Underline,
    History,
    Placeholder
} from 'tiptap-extensions'

export default {
    components: {
        EditorMenuBar,
        EditorContent
    },
    props: {
        section: Object,
        value: String
    },
    data() {
        return {
            editor: null,
            changesMade: false,
            focus: false
        }
    },
    computed: {
        placeholder() {
            return this.section.placeholder
                ? this.section.placeholder
                : this.$t('PLACEHOLDER_TYPE_HERE')
        }
    },
    methods: {
        focusInput() {
            this.$refs.textInput.focus()
        },
        handleClick() {
            this.focus = true
            this.$emit('handleClick')
        },
        blur() {
            this.focus = false
        },
        confirmInput(entered) {
            this.focus = false
            if (
                this.changesMade == true &&
                (entered === true || (entered !== true && !this.blockBlur))
            ) {
                this.$emit('confirmInput', this.section, entered)
            } else {
                this.$emit('blur', this.section, entered)
            }
            this.changesMade = false
        },
        onInput(html) {
            this.changesMade = true
            this.$emit('input', html)
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.editor = new Editor({
                autoFocus: true,
                content: this.value,
                extensions: [
                    new Blockquote(),
                    new BulletList(),
                    new CodeBlock(),
                    new HardBreak(),
                    new Heading({ levels: [1, 2, 3] }),
                    new HorizontalRule(),
                    new ListItem(),
                    new OrderedList(),
                    new TodoItem(),
                    new TodoList(),
                    new Link(),
                    new Bold(),
                    new Code(),
                    new Italic(),
                    new Strike(),
                    new Underline(),
                    new History(),
                    new Placeholder({
                        emptyEditorClass: 'is-editor-empty',
                        emptyNodeClass: 'is-empty',
                        emptyNodeText: 'Write something …',
                        showOnlyCurrent: true
                    })
                ],
                onUpdate: ({ getHTML }) => {
                    this.onInput(getHTML())
                }
            })
        })
    },
    beforeDestroy() {
        this.editor.destroy()
    }
}
</script>
<style lang="scss" scoped>
i {
    color: var(--color);
}
.editor {
    position: relative;
    width: 100%;
    font-size: 1rem;
}
.menubar {
    transition: visibility 0.2s 0.4s, opacity 0.2s 0.4s;
    display: flex;

    button {
        font-size: 1rem;
    }

    &.is-hidden {
        visibility: hidden;
        opacity: 0;
    }

    &.is-focused {
        visibility: visible;
        opacity: 1;
        transition: visibility 0.2s, opacity 0.2s;
    }

    &__button {
        font-weight: bold;
        display: flex;
        background: transparent;
        border: 0;
        color: $input;
        margin-right: 0.2rem;
        border-radius: 3px;
        cursor: pointer;

        &:hover {
            background-color: rgba($input, 0.05);
        }

        &.is-active {
            background-color: rgba($input, 0.1);
        }
        i {
            color: var(--color);
            font-size: 1.2rem;
        }
    }

    span#{&}__button {
        font-size: 2rem;
    }
}
.editor p.is-editor-empty:first-child::before {
    content: attr(data-empty-text);
    float: left;
    color: #aaa;
    pointer-events: none;
    height: 1rem;
    font-style: italic;
}
.editor__content {
    border: $input-border;
    border-radius: $input-radius;
    margin-top: 0.5rem;
    padding: 0 0.5rem;
    box-sizing: border-box;
    position: relative;

    &.-placeholder {
        &::after {
            content: attr(data-content);
            float: left;
            color: #aaa;
            pointer-events: none;
            height: 1rem;
            font-style: italic;
            position: absolute;
            top: 1rem;
            left: 1rem;
        }
    }
}
.menubar__button {
    color: var(--color);
}
</style>
